// Closing the account, from the account page (PLAN-2026-10-31-deleting-an-account,
// DIRECTION A28, B-596).
//
// SAID BEFORE IT HAPPENS (CLAUDE.md, "say what will happen"): what goes and
// what stays, in plain words, at the moment of the decision, and a copy of
// the data offered first, since nothing comes back. Then the password, as the
// big sites ask before closing an account, with a link to set a new one for
// somebody who has forgotten it. Nothing else is asked.

import { useState } from 'react';
import { useSession } from '../../data/session';
import { Button, Dialog, Line, PasswordField, Stack, Text, tell } from '../../kit/ui';
import '../../styles/screens/account.css';

export function DeleteAccountSheet({
  opened,
  onClose,
  email,
  onDownload,
  onDeleted,
}: {
  opened: boolean;
  onClose: () => void;
  /** Where a link to set a new password goes, for a forgotten one. */
  email: string | null;
  /** The account page's download of the person's data. */
  onDownload: () => void;
  onDeleted: () => void;
}) {
  const { deleteAccount, requestReset } = useSession();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [linkSent, setLinkSent] = useState(false);

  const close = () => {
    setPassword('');
    setError(null);
    setLinkSent(false);
    onClose();
  };

  const destroy = () => {
    if (busy || password.length === 0) return;
    setBusy(true);
    void (async () => {
      const outcome = await deleteAccount(password);
      setBusy(false);
      if (!outcome.ok) {
        // The sheet stays open: closing it on a failure would leave somebody
        // believing the account is gone when it is not. A wrong password is
        // said under the field it is about.
        // An account with no password yet (its address confirmed by a
        // sign-in code) is told the same way, beside the link that sets one.
        if (outcome.code === 'forbidden' || outcome.code === 'conflict') setError(outcome.message);
        else tell(outcome.message, 'bad');
        return;
      }
      setPassword('');
      onDeleted();
    })();
  };

  const sendLink = () => {
    if (email === null) return;
    void (async () => {
      const outcome = await requestReset(email);
      if (outcome.ok) setLinkSent(true);
      else tell(outcome.message, 'bad');
    })();
  };

  return (
    <Dialog opened={opened} onClose={close} title="Delete your account?">
      <Stack gap={3}>
        <Text block>It happens at once, and it cannot be undone.</Text>
        <Stack gap={1}>
          <Text weight="strong" block>
            What goes
          </Text>
          <ul className="fsp-privacy-list">
            <li>
              <Text>Your account and how you sign in.</Text>
            </li>
            <li>
              <Text>
                What you said in your leagues: messages, reactions and poll votes. A reply to one of
                yours will say it is gone.
              </Text>
            </li>
            <li>
              <Text>Your achievements, starred players, saved faces and settings.</Text>
            </li>
          </ul>
        </Stack>
        <Stack gap={1}>
          <Text weight="strong" block>
            What stays
          </Text>
          <ul className="fsp-privacy-list">
            <li>
              <Text>
                Your teams. A co-manager takes yours over; if there is none, a bot plays it during
                the season, and between seasons its seat opens. A league with nobody else in it
                closes.
              </Text>
            </li>
            <li>
              <Text>
                A league you run passes to its co-commissioner, or its managers choose one.
              </Text>
            </li>
            <li>
              <Text>
                Each league’s record, showing a former manager. A vote it has already decided keeps
                its count, and a few of its own messages, such as one about a rule change you
                proposed, keep your name.
              </Text>
            </li>
          </ul>
        </Stack>
        <Line gap={2} wrap>
          <Text tone="secondary" size="sm">
            Want a copy of your data first?
          </Text>
          <Button size="xs" icon="download" onClick={onDownload}>
            Download as JSON
          </Button>
        </Line>
        <PasswordField
          label="Your password"
          value={password}
          onChange={(v) => {
            setPassword(v);
            setError(null);
          }}
          error={error}
        />
        {email === null ? null : linkSent ? (
          <Text tone="secondary" size="sm" block>
            {`A link to set a new password is on its way to ${email}.`}
          </Text>
        ) : (
          <Text size="sm" block>
            <button type="button" className="fsp-linklike" onClick={sendLink}>
              Forgot it? Email me a link to set a new one.
            </button>
          </Text>
        )}
        <Line end>
          <Button variant="quiet" onClick={close}>
            Keep it
          </Button>
          <Button
            variant="danger"
            loading={busy}
            disabled={password.length === 0}
            onClick={destroy}
          >
            Delete for good
          </Button>
        </Line>
      </Stack>
    </Dialog>
  );
}
